import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';
import { KioskTodayHourlyChart } from './KioskTodayHourlyChart.jsx';
import { ShiftDashboardView } from './ShiftDashboardView.jsx';
import { kioskApi } from '../lib/kiosk-api.js';
import { KioskEventsStrip } from './KioskEventsStrip.jsx';
import { MorningParPanel } from './MorningParPanel.jsx';

// Open-Meteo weather code → emoji (used only here).
function kioskWeatherEmoji(code) {
  const c = Number(code);
  if (c === 0 || c === 1) return '☀️';
  if (c === 2) return '⛅';
  if (c === 3) return '☁️';
  if (c === 45 || c === 48) return '🌫';
  if (c >= 51 && c <= 57) return '🌦';
  if (c >= 61 && c <= 67) return '🌧';
  if (c >= 71 && c <= 77) return '❄️';
  if (c >= 80 && c <= 82) return '🌧';
  if (c >= 85 && c <= 86) return '❄️';
  if (c >= 95) return '⛈';
  return '🌤';
}

export function KioskTodayView({ session }) {
  const locKey = session.location.key;
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const d = await kioskApi('GET', `/api/kiosk/${locKey}/dashboard`, null, session.token);
      setData(d); setErr('');
    } catch (e) { setErr(e.message || String(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);

  if (loading) return <div style={{ fontSize: 13, color: '#9e9e9e', padding: 16 }}>Loading today's report…</div>;
  if (err) return <div style={{ background: '#fee', padding: 12, borderRadius: 8, color: '#b91c1c', fontSize: 13 }}>{err}</div>;
  if (!data) return null;

  const sc = data.scorecard || {};
  const w = data.weather || {};
  const tx = sc.transactions || {};
  const txDelta = tx.deltaPct;
  const flagColor = (f) => f === 'green' ? '#16a34a' : f === 'red' ? '#dc2626' : (f === 'yellow' || f === 'orange') ? '#d97706' : '#9e9e9e';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Weather strip */}
      {w.available && w.today && (
        <div style={{ background: 'var(--ink, #1f2a44)', color: '#fff', borderRadius: 12, padding: 14,
                       display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ fontSize: 36, lineHeight: 1, flexShrink: 0 }}>{kioskWeatherEmoji(w.today.weather_code)}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 11, color: 'var(--tea-gold, #b48a47)', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>Today's weather</div>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              {w.today.temp_high}°/{w.today.temp_low}° · {w.today.description}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)' }}>
              {w.today.precipitation_pct}% precip · {w.today.wind_speed} mph wind
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {(w.days || []).slice(1, 6).map((d, i) => (
              <div key={i} style={{ textAlign: 'center', fontSize: 10, color: 'rgba(255,255,255,0.7)' }}>
                <div>{new Date(d.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short' })}</div>
                <div style={{ fontSize: 18, lineHeight: 1.1 }}>{kioskWeatherEmoji(d.weather_code)}</div>
                <div>{d.temp_high}°</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <KioskEventsStrip events={data.events} />

      {/* Morning PAR — same drop-down as the website's Daily tab (CJ 2026-10-19). */}
      <MorningParPanel location={session.location} kioskToken={session.token} />

      {/* Transactions today — single big tile, no dollar amounts. */}
      <div className="card" style={{ padding: 18 }}>
        <div style={{ fontSize: 11, color: '#9e9e9e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Transactions today</div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginTop: 4 }}>
          <div style={{ fontSize: 40, fontWeight: 700, color: '#212121', lineHeight: 1 }}>
            {(tx.value || 0).toLocaleString()}
          </div>
          {txDelta != null && (
            <div style={{ fontSize: 13, fontWeight: 600, color: flagColor(tx.flag) }}>
              {txDelta > 0 ? '+' : ''}{Number(txDelta).toFixed(1)}% <span style={{ color: '#9e9e9e', fontWeight: 400 }}>vs same time last week</span>
            </div>
          )}
        </div>
      </div>

      {/* Hourly volume chart — transaction count only, no dollars. */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#212121' }}>Hourly transactions</div>
          <div style={{ fontSize: 10, color: '#9e9e9e' }}>
            {data.meta?.forecastSource === 'pis_hourly_forecast' ? 'Forecast: PIS' : 'Forecast: last-week actuals'}
          </div>
        </div>
        <KioskTodayHourlyChart hourly={data.hourlyChart || []} />
        <div style={{ display: 'flex', gap: 14, fontSize: 11, color: '#757575', marginTop: 6 }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#5D4037', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Actual</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#1565c0', borderRadius: '50%', marginRight: 4, verticalAlign: 'middle' }} />Forecast</span>
        </div>
      </div>

      {/* Decision guide — already written without dollar specifics. */}
      {data.decisionGuide && (data.decisionGuide.headline || data.decisionGuide.detail) && (
        <div className="card" style={{ background: '#fffbeb', borderLeft: '4px solid #d97706' }}>
          <div style={{ fontSize: 11, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 700 }}>Right now</div>
          {data.decisionGuide.headline && <div style={{ fontSize: 14, fontWeight: 600, color: '#78350f', marginTop: 4 }}>{data.decisionGuide.headline}</div>}
          {data.decisionGuide.detail && <div style={{ fontSize: 12, color: '#78350f', marginTop: 4 }}>{data.decisionGuide.detail}</div>}
        </div>
      )}

      {/* Shift roster at the bottom — same view the regular site's Daily
          report shows. kioskToken makes the station picker active so any
          staff at the iPad can move themselves to Bar / Register / Food /
          Back Bar without going through a manager. */}
      <ShiftDashboardView location={session.location} user={session.user}
        kioskToken={session.token} />
    </div>
  );
}
